// FeatureDisabledScreen
//
// Rendered in place of a feature's screen when its GLOBAL platform flag
// (system_settings.<key>) is off. Distinct from MedicationsOptInGate-style
// per-account opt-in walls — this surface communicates that the feature is
// platform-disabled by Eleplan (incident response, staged rollout, etc.),
// not that the user needs to take an action.
//
// Stays brief and honest. The flag key + name are passed in so the surface
// can be reused by every gated screen without writing per-feature copy.

import React from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '../hooks/useTheme';
import { useDesktopContentMaxWidth } from '../utilities/desktopContent';
import Icon from './Icon';

interface Props {
  // Human-readable name shown in the title. Plain English, no jargon.
  featureName: string;
  // Optional override for the body copy. Default fits most cases.
  description?: string;
  // For diagnostics + bug reports — surfaced quietly at the bottom.
  flagKey?: string;
}

const DEFAULT_BODY =
  "We've temporarily turned this off. It'll be back shortly — your data is safe and untouched.";

export default function FeatureDisabledScreen({
  featureName,
  description,
  flagKey,
}: Props) {
  const theme = useTheme();
  const desktopContent = useDesktopContentMaxWidth();

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: theme.colors.background }]} edges={['top', 'left', 'right']}>
      <ScrollView
        contentContainerStyle={[styles.scroll, desktopContent]}
        showsVerticalScrollIndicator={false}
      >
        <View style={[styles.iconWrap, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}>
          <Icon icon={['fad', 'pause-circle']} size={36} color={theme.colors.muted} />
        </View>
        <Text style={[styles.title, { color: theme.colors.text }]}>
          {featureName} is paused
        </Text>
        <Text style={[styles.body, { color: theme.colors.muted }]}>
          {description || DEFAULT_BODY}
        </Text>
        {flagKey ? (
          <Text style={[styles.flagKey, { color: theme.colors.muted }]}>
            {flagKey}
          </Text>
        ) : null}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  scroll: {
    padding: 24,
    paddingTop: 64,
    width: '100%',
    alignItems: 'center',
  },
  iconWrap: {
    width: 84,
    height: 84,
    borderRadius: 42,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
  },
  body: {
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'center',
    maxWidth: 400,
  },
  flagKey: {
    fontSize: 11,
    marginTop: 32,
    opacity: 0.5,
    fontFamily: 'Menlo, Consolas, monospace',
  },
});
